// The bibliography tab: auto-extract a .bib from the sources, edit it as plain
// text (lazy CodeMirror), search Crossref/arXiv for more references, and click an
// entry to insert \cite at the cursor. references render in the PDF via biber.
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  extractReportBib,
  getReportBib,
  putReportBib,
  searchReportBib,
  type ReportBib,
  type ReportBibCandidate,
} from "@/api/report";
import { Alert, Button, Spinner } from "@/components/ui";

const CodeMirrorEditor = lazy(() => import("./codemirror-editor"));

/** The bibliography endpoints this panel drives — defaults to the report's; the
 *  project surface passes the project-level ones (same shapes). */
export interface BibApi {
  get: (id: string) => Promise<ReportBib>;
  extract: (id: string) => Promise<ReportBib>;
  put: (id: string, src: string) => Promise<ReportBib>;
  search: (
    id: string,
    body: { query: string; source?: "all" | "crossref" | "arxiv"; limit?: number },
  ) => Promise<{ candidates: ReportBibCandidate[] }>;
}

const REPORT_BIB_API: BibApi = {
  get: getReportBib,
  extract: extractReportBib,
  put: putReportBib,
  search: searchReportBib,
};

export function BibEditorPanel({
  projectId,
  onInsertCite,
  bibApi = REPORT_BIB_API,
  scope = "report",
}: {
  projectId: string;
  onInsertCite?: (key: string) => void;
  bibApi?: BibApi;
  scope?: string;
}) {
  const { t } = useTranslation();
  const draft = useRef("");
  const dirty = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seed, setSeed] = useState(0); // bump to remount the editor with new text
  const [filter, setFilter] = useState("");

  // online search
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState<ReportBibCandidate[]>([]);

  const { data: bib, refetch } = useQuery({
    queryKey: [scope + "-bib", projectId],
    queryFn: () => bibApi.get(projectId),
    retry: false,
  });

  useEffect(() => {
    if (bib && !dirty.current) {
      draft.current = bib.bib_source;
      setSeed((s) => s + 1);
    }
  }, [bib]);

  const existingKeys = useMemo(
    () => new Set((bib?.entries ?? []).map((e) => e.key)),
    [bib],
  );
  const entries = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = bib?.entries ?? [];
    if (!q) return all;
    return all.filter(
      (e) => e.key.toLowerCase().includes(q) || (e.title || "").toLowerCase().includes(q),
    );
  }, [bib, filter]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await bibApi.put(projectId, draft.current);
      dirty.current = false;
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not save the bibliography."));
    } finally {
      setBusy(false);
    }
  };

  const extract = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await bibApi.extract(projectId);
      draft.current = res.bib_source;
      dirty.current = false;
      setSeed((s) => s + 1);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not extract citations."));
    } finally {
      setBusy(false);
    }
  };

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setError(null);
    try {
      const res = await bibApi.search(projectId, { query: q });
      setCandidates(res.candidates);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Citation search failed."));
    } finally {
      setSearching(false);
    }
  };

  // Append a candidate's BibTeX to the .bib and persist. Dedups the cite key.
  const addCandidate = async (c: ReportBibCandidate) => {
    let bibtex = c.bibtex;
    if (existingKeys.has(c.key)) {
      let n = 1;
      let key = `${c.key}${String.fromCharCode(97 + n)}`;
      while (existingKeys.has(key)) {
        n += 1;
        key = `${c.key}${String.fromCharCode(97 + n)}`;
      }
      bibtex = c.bibtex.replace(`{${c.key},`, `{${key},`);
    }
    const next = `${draft.current.trimEnd()}\n\n${bibtex}\n`;
    setBusy(true);
    try {
      const res = await bibApi.put(projectId, next);
      draft.current = res.bib_source;
      dirty.current = false;
      setSeed((s) => s + 1);
      setCandidates((cs) => cs.filter((x) => x !== c));
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not add the reference."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" disabled={busy} onClick={extract}>
          {busy ? <Spinner className="h-3 w-3" /> : t("Auto-extract from sources")}
        </Button>
        <Button size="sm" disabled={busy} onClick={save}>
          {t("Save .bib")}
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}

      {/* Online citation search (Crossref / arXiv) */}
      <div className="rounded-lg border border-black/10 p-2">
        <div className="flex items-center gap-1">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder={t("Find references online (Crossref / arXiv)…")}
            className="flex-1 rounded-md border border-black/10 px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
          />
          <Button size="sm" variant="secondary" disabled={searching || !query.trim()} onClick={runSearch}>
            {searching ? <Spinner className="h-3 w-3" /> : t("Search")}
          </Button>
        </div>
        {candidates.length > 0 && (
          <ul className="mt-2 max-h-40 space-y-1 overflow-auto">
            {candidates.map((c, i) => (
              <li key={`${c.key}-${i}`} className="flex items-start gap-1 text-[11px]">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => addCandidate(c)}
                  className="mt-0.5 rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-brand-700"
                  title={t("Add to bibliography")}
                >
                  +
                </button>
                <span className="text-ink-soft">
                  <span className="font-medium text-ink">{c.title}</span>
                  {c.authors ? ` — ${c.authors.split(" and ")[0]}` : ""}
                  {c.year ? ` (${c.year})` : ""}{" "}
                  <span className="text-ink-faint">[{c.source}]</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-[10px] text-ink-faint">
          {t("Searches send your query to Crossref and arXiv.")}
        </p>
      </div>

      {/* Parsed entries — filter + click to insert \cite */}
      {(bib?.entries.length ?? 0) > 0 && (
        <div className="rounded-lg bg-zinc-50 p-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("Filter citations…")}
            className="mb-1 w-full rounded-md border border-black/10 px-2 py-1 text-[11px] focus:border-brand-400 focus:outline-none"
          />
          <div className="max-h-24 overflow-auto text-[11px] text-ink-soft">
            {entries.map((e) => (
              <button
                key={e.key}
                type="button"
                onClick={() => onInsertCite?.(e.key)}
                disabled={!onInsertCite}
                className="block w-full truncate rounded px-1 py-0.5 text-left hover:bg-brand-50 disabled:cursor-default disabled:hover:bg-transparent"
                title={onInsertCite ? t("Insert \\cite at cursor") : undefined}
              >
                <span className="font-mono text-brand-700">{e.key}</span>
                {e.title ? ` — ${e.title}` : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 rounded-lg border border-black/10">
        <Suspense fallback={<div className="flex h-full items-center justify-center"><Spinner /></div>}>
          <CodeMirrorEditor
            key={seed}
            language="plain"
            initialValue={draft.current}
            onChange={(v) => {
              draft.current = v;
              dirty.current = true;
            }}
          />
        </Suspense>
      </div>
    </div>
  );
}
